import axios from 'axios';

import { CONFIG } from 'src/config-global';

import SafeStorage from './safe-storage';
import { JWT_KEY, USER_KEY } from './constant';

const getTimezone = () => {
  try {
    if (typeof window !== 'undefined') {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    }
  } catch {
    /* empty */
  }
  return 'UTC'; // SSR fallback
};
const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
/* ----------------------------- Instances ----------------------------- */
// Axios' own default is `timeout: 0` (wait forever) - neither instance ever
// set one, so a stalled connection (not an HTTP error, just a network hang)
// left calling code with no way to know the request would never resolve.
const REQUEST_TIMEOUT_MS = 20000;

const axiosInstance = axios.create({
  baseURL: CONFIG.serverUrl,
  timeout: REQUEST_TIMEOUT_MS,
  // withCredentials: true,
});

export const newAxiosInstance = axios.create({
  baseURL: CONFIG.serverUrl1,
  timeout: REQUEST_TIMEOUT_MS,
  // withCredentials: true,
});

/* ------------------------------ Helpers ------------------------------ */
/**
 * The API's `domain` param/header identifies the FusionPBX telephony domain
 * (e.g. voip.voicesphereai.com.au or, for a real tenant subdomain,
 * sr.voicesphereai.com.au) - NOT necessarily the dashboard's own access URL.
 *
 * On a real tenant hostname, src/middleware.js sets a `tenant_domain` cookie
 * to that hostname (which IS the tenant's sip_domain, by construction - see
 * tenants/management/commands/sync_tenant_domains.py on the backend), so we
 * use that instead of guessing from the browser's address bar. On the
 * original single-tenant deployment (phone.voicesphereai.com.au / localhost)
 * the middleware sets no cookie, so this falls back to the configured
 * NEXT_PUBLIC_DOMAIN_NAME exactly as before.
 */
const getTenantDomainFromCookie = () => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|; )tenant_domain=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

const getDomainFromLocation = () => getTenantDomainFromCookie() || CONFIG.appDomain;

/* ---------------------------- Interceptors --------------------------- */
function attachInterceptors(instance) {
  instance.interceptors.request.use(
    (config) => {
      const token = SafeStorage.getSessionItem(JWT_KEY);
      const userData = SafeStorage.getSessionJSON(USER_KEY);

      let agent;
      let extensions;
      try {
        agent = userData?.uuid;
        extensions = userData?.extension;
      } catch {
        agent = undefined;
        extensions = undefined;
      }

      config.headers = config.headers || {};

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // 👇 always set domain + tz in headers
      config.headers['X-Domain'] = getDomainFromLocation();
      config.headers['X-Timezone'] = getTimezone();

      if (agent) config.headers['X-Agent'] = agent;
      if (extensions) config.headers['X-Extensions'] = extensions;

      // Normalize method
      const method = (config.method || 'get').toLowerCase();

      // Endpoints that derive scope from the JWT alone reject these as
      // input: the tenant-scoped APIs treat `domain`/`extensions` as a
      // caller trying to choose whose data to read, and answer 400 rather
      // than ignoring them. The legacy endpoints, in contrast, still READ
      // them, so they cannot simply be dropped for everyone.
      //
      // `skipContextParams: true` opts one request out of the injection -
      // nothing else changes, and the identifying headers (X-Domain,
      // X-Agent, X-Extensions) are still sent, because they are read by
      // middleware rather than validated as query input.
      const skipContextParams = config.skipContextParams === true;

      if (skipContextParams) {
        return config;
      }

      // keep your body injection logic for non-GET

      if (method === 'get') {
        config.params = {
          ...(config.params || {}),
          ...(agent && { agent }),
          ...(extensions && { extensions }),
          domain: getDomainFromLocation(),
        };
      } else if (['post', 'put', 'delete', 'patch'].includes(method)) {
        // Check if data is FormData - if so, append fields directly without spreading
        if (config.data instanceof FormData) {
          if (agent) config.data.append('agent', agent);
          config.data.append('domain', getDomainFromLocation());
        } else if (typeof config.data === 'object' && config.data !== null) {
          config.data = {
            ...config.data,
            ...(agent && { agent }),
            domain: getDomainFromLocation(),
          };
        } else {
          config.data = { ...(agent && { agent }), domain: getDomainFromLocation() };
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Add null safety checks
      if (!error) {
        return Promise.reject(new Error('An unknown error occurred'));
      }

      // Handle 401 Unauthorized errors
      if (error?.response?.status === 401) {
        // Clear session storage
        try {
          SafeStorage.clearSession();
        } catch (storageError) {
          console.error('Error clearing session storage:', storageError);
        }

        // Only redirect if we're in a browser environment
        if (typeof window !== 'undefined') {
          try {
            // Store the current path to redirect back after login
            const currentPath = window.location.pathname;
            if (currentPath !== '/auth/jwt/sign-in') {
              SafeStorage.setSessionItem('redirectPath', currentPath);
            }

            // Redirect to login page
            window.location.href = '/auth/jwt/sign-in';
          } catch (redirectError) {
            console.error('Error during redirect:', redirectError);
          }
        }
      }

      // Safely extract error message with fallback
      // Check for full object first, then nested message property
      const errorMessage =
        error?.response?.data ||
        error?.response?.data?.message ||
        error?.message ||
        'Something went wrong, Please try again later.';

      return Promise.reject(errorMessage);
    }
  );
}

attachInterceptors(axiosInstance);
attachInterceptors(newAxiosInstance);

export default axiosInstance;

/* ------------------------- Fetchers / Mutators ------------------------ */
export const fetcher = async (args) => {
  try {
    if (!args) {
      throw new Error('Fetcher called with invalid arguments');
    }
    const [url, config] = Array.isArray(args) ? args : [args];

    if (!url) {
      throw new Error('Fetcher called with invalid URL');
    }

    const res = await axiosInstance.get(url, { ...config });
    return res?.data || null;
  } catch (error) {
    // Enhanced error handling with null safety
    const errorData = error?.response?.data;
    const errorMessage = error?.message;

    throw errorData || errorMessage || 'NETWORK ERROR';
  }
};

export const newFetcher = async (args) => {
  try {
    if (!args) {
      throw new Error('newFetcher called with invalid arguments');
    }
    const [url, config] = Array.isArray(args) ? args : [args];

    if (!url) {
      throw new Error('newFetcher called with invalid URL');
    }

    const res = await newAxiosInstance.get(url, { ...config });
    return res?.data || null;
  } catch (error) {
    // Enhanced error handling with null safety
    const errorData = error?.response?.data;
    const errorMessage = error?.message;

    throw errorData || errorMessage || 'NETWORK ERROR';
  }
};

export const handleMutateRequest = async (method, url, payload) => {
  if (!method || !url) {
    throw new Error('handleMutateRequest requires method and url');
  }

  const config = {
    method,
    url,
    ...(payload && { data: payload }), // body only
  };

  const response = await newAxiosInstance.request(config);
  return response?.data || null;
};

/* -------------------------------- Routes ------------------------------ */
export const endpoints = {
  chat: {
    whatsapp: '/chat/whatsapp/',
    'whatsapp-list': '/chat/whatsapp/list/',
    sms: '/chat/sms/',
    'sms-list': '/chat/sms/list/',
  },
  whatsapp: {
    template: '/chat/whatsapp/templates/',
  },
  dashboard: {
    cardsData: 'dashboard_cards/',
    trafficByProducts: 'dashboard_TrafficByProduct_graph/',
    trafficByLanguages: 'dashboard_TrafficByLanguage_graph/',
    callByDay: 'dashboard_CallByDay_graph/',
    agentDashboard: '/api/agent-dashboard/',
    adminDashboard: '/api/admin-dashboard/',
    adminDashboardQueue: '/api/admin-dashboard-queue-performance/',
    agentDashboardQueue: '/api/agent-dashboard-queue-performance/',
    agentRecentActivity: '/api/agent-recent-activity/',
  },
  call: {
    logs: 'call_logs/',
    newlog: '/api/call-logs/',
    details: '/api/call-logs/',
    transcript: '/api/call-transcription/',
    'transcript-aws': '/api/call-transcription-aws/',
    'transcript-service': '/api/call-transcription/',
    'ai-analysis': '/api/ai-analysis/',
    report: '/api/report/',
    'petpooja-call-logs': '/petpooja/call-logs/',
    'active-calls': '/api/active-calls/',
    'agent-status': '/api/agent-status/',
    'agent-talktime': '/api/agent-talktime-report/',
    'hang-up': '/api/hangup-call/',
    'submit-barz': '/api/eavesdrop-call/',
    'recordings-search': '/api/call-recordings/search/',
    'imported-call-logs': '/api/imported-call-logs/',
    'imported-call-logs-headers': '/api/imported-call-logs/headers/',
    'imported-call-logs-bulk-transcribe': '/api/imported-call-logs/transcribe/bulk/',
    'imported-call-logs-transcribe-progress': '/api/imported-call-logs/transcribe/progress/',
  },
  users: {
    list: '/api/users/list/',
    dynamicPoints: (uuid, dynamicPoint) => `/api/users/${uuid}/${dynamicPoint}`,
    changePassword: 'change-password/',
    profile: 'profile/',
    callLogs: 'call-logs/',
    permission: '/core/permissions/',
  },
  auth: {
    me: '/api/auth/me',
    signIn: `${CONFIG.serverUrl}/api/users/token/`,
    signUp: '/api/auth/sign-up/',
  },
  queue: {
    'admin-queue-data': '/api/admin-queue-details/',
    'agent-queue-data': '/api/agent-queue-details/',
  },

  knowledge: {
    'knowledge-base': '/api/knowledge-base/',
  },
  voicemail: {
    list: '/api/voicemails/',
  },
  analytics: {
    configuration: '/api/analytics-configuration/',
  },
  tasks: {
    list: '/api/tasks/',
  },
  languages: {
    'languages-configuration': '/api/language-configuration/',
  },
  contacts: {
    list: '/api/contacts/',
    detail: (uuid) => `/api/contacts/${uuid}/`,
  },
  cnam: {
    lookup: (domain, phone) => `/api/cnam-lookup/?domain=${domain}&phone=${phone}`,
  },
  // Tenant Portal -> Settings -> API Integration. None of these paths
  // carries a tenant identifier: the backend resolves the tenant from the
  // session's JWT, so there is nothing here for the browser to change.
  apiIntegration: {
    root: '/tenants/api-integration/',
    createKey: '/tenants/api-integration/keys/',
    regenerateKey: (keyUuid) => `/tenants/api-integration/keys/${keyUuid}/regenerate/`,
    revokeKey: (keyUuid) => `/tenants/api-integration/keys/${keyUuid}/revoke/`,
  },
  platformAdmin: {
    provisionRequests: '/tenants/provisioning/requests/',
    provisionRequestDetail: (uuid) => `/tenants/provisioning/requests/${uuid}/`,
    provisionRequestLogs: (uuid) => `/tenants/provisioning/requests/${uuid}/logs/`,
    provisionRequestCompleteStep: (uuid) => `/tenants/provisioning/requests/${uuid}/complete-step/`,
    provisionRequestRetry: (uuid) => `/tenants/provisioning/requests/${uuid}/retry/`,
    provisionRequestCancel: (uuid) => `/tenants/provisioning/requests/${uuid}/cancel/`,
    tenants: '/tenants/admin/',
    tenantDetail: (uuid) => `/tenants/admin/${uuid}/`,
    tenantSettings: (uuid) => `/tenants/admin/${uuid}/settings/`,
    tenantUsersSummary: (uuid) => `/tenants/admin/${uuid}/users-summary/`,
    tenantUsers: (tenantUuid) => `/tenants/admin/${tenantUuid}/users/`,
    tenantUserDetail: (tenantUuid, userUuid) => `/tenants/admin/${tenantUuid}/users/${userUuid}/`,
    tenantUserDisable: (tenantUuid, userUuid) =>
      `/tenants/admin/${tenantUuid}/users/${userUuid}/disable/`,
    tenantUserEnable: (tenantUuid, userUuid) =>
      `/tenants/admin/${tenantUuid}/users/${userUuid}/enable/`,
    tenantUserResetPassword: (tenantUuid, userUuid) =>
      `/tenants/admin/${tenantUuid}/users/${userUuid}/reset-password/`,
    tenantUserRoles: (tenantUuid, userUuid) =>
      `/tenants/admin/${tenantUuid}/users/${userUuid}/roles/`,
    tenantUserSyncPreview: (tenantUuid) => `/tenants/admin/${tenantUuid}/users/sync-preview/`,
    tenantUserSync: (tenantUuid) => `/tenants/admin/${tenantUuid}/users/sync/`,
    // Tenant API Management. The overview is cross-tenant (Platform Admin
    // only); every mutation names its tenant in the path, because a Platform
    // Admin legitimately acts on any tenant - unlike the tenant-facing
    // /tenants/api-integration/ routes, which carry no tenant at all.
    tenantApiKeysOverview: '/tenants/admin/api-keys/',
    tenantApiCatalogue: '/tenants/admin/api-catalogue/',
    tenantApiKeys: (tenantUuid) => `/tenants/admin/${tenantUuid}/api-keys/`,
    tenantApiKeyRegenerate: (tenantUuid, keyUuid) =>
      `/tenants/admin/${tenantUuid}/api-keys/${keyUuid}/regenerate/`,
    tenantApiKeyRevoke: (tenantUuid, keyUuid) =>
      `/tenants/admin/${tenantUuid}/api-keys/${keyUuid}/revoke/`,
    rolesCatalog: '/tenants/roles/',
    systemHealth: '/operations/system-health/',
    auditLogs: '/operations/audit-logs/',
    auditLogsExport: '/operations/audit-logs/export/',
    liveCalls: '/operations/live-calls/',
    liveCallsExport: '/operations/live-calls/export/',
    sipRegistrations: '/operations/sip-registrations/',
    sipRegistrationsExport: '/operations/sip-registrations/export/',
    voiceQuality: '/operations/voice-quality/',
    voiceQualityExport: '/operations/voice-quality/export/',
    tenantHealth: '/operations/tenant-health/',
    diagnostics: '/operations/diagnostics/',
    activeAlerts: '/operations/active-alerts/',
    // Cross-tenant CDR list for the Platform Admin Call History page.
    // Server-side paginated/filtered - carries only what the table shows.
    callHistory: '/operations/calls/',
    // Phase 2 Call Details. Both are lazy - nothing here is fetched by a
    // list page; the drawer/detail page requests them on open.
    callDetail: (callUuid) => `/operations/calls/${callUuid}/`,
    // Streamed through the API, so the recording's filesystem path never
    // reaches the browser.
    callRecording: (callUuid) => `/operations/calls/${callUuid}/recording/`,
    // POST { call_ids: [...] } -> a ZIP of several calls' recordings, for
    // Call History's "Download Selected Recordings" button.
    callRecordingsBulkDownload: '/operations/calls/recordings/bulk-download/',
    platformAdmins: '/accounts/platform-admins/',
    platformAdminDetail: (id) => `/accounts/platform-admins/${id}/`,
    allUsers: '/tenants/admin/users/',
  },
};
